import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Compass,
  Heart,
  Landmark,
  Loader2,
  Luggage,
  Mountain,
  Sparkles,
  UsersRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageMeta } from "@/components/PageMeta";
import { Chip } from "@/components/funnel/Chip";
import { ProgressLabel } from "@/components/funnel/ProgressLabel";
import { FunnelHero, FunnelHowItWorks } from "@/components/funnel/FunnelMarketing";
import {
  FUNNEL_STORAGE_KEY,
  QUIZ_INTERESTS,
  STAGE_ORDER,
  readSavedDraft,
  type Budget,
  type FunnelStage,
  type Pace,
} from "@/components/funnel/funnel-draft";
import { apiRequest, queryClient, seedAuthUserCache } from "@/lib/queryClient";
import { useAuth } from "@/hooks/use-auth";
import { TRAVELER_ARCHETYPES } from "@/lib/traveler-archetypes";
import { getFunnelPlannerDestination } from "@/lib/funnel-navigation";
import type { TravelerProfile } from "@shared/schema";

const FUNNEL_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "TROOPN",
  alternateName: "Troopn",
  url: "https://troopn.com",
  logo: "https://troopn.com/troopn-logo.png",
  description: "Set your travel preferences, discover experiences, and build your next trip with AI.",
};

const ARCHETYPE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  heart: Heart,
  backpack: Luggage,
  users: UsersRound,
  mountain: Mountain,
  coffee: Compass,
  accessibility: Landmark,
};

export default function FunnelLanding() {
  const [, navigate] = useLocation();
  const { user, isAuthenticated, isLoading } = useAuth();
  const [savedDraft] = useState(readSavedDraft);
  const [stage, setStage] = useState<FunnelStage>(savedDraft.stage ?? "signup");
  const [archetypeId, setArchetypeId] = useState<string | null>(savedDraft.archetypeId ?? null);
  const [pace, setPace] = useState<Pace | null>(savedDraft.pace ?? null);
  const [budget, setBudget] = useState<Budget | null>(savedDraft.budget ?? null);
  const [interests, setInterests] = useState<string[]>(savedDraft.interests ?? []);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [preferencesSaved, setPreferencesSaved] = useState(false);
  const [saveAfterAuth, setSaveAfterAuth] = useState(savedDraft.saveAfterAuth === true);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const initialRestorationValidated = useRef(false);

  const existingProfile = (user?.travelerProfile ?? {}) as TravelerProfile;
  const draftProfile = useMemo((): TravelerProfile => {
    const archetype = TRAVELER_ARCHETYPES.find((item) => item.id === archetypeId);
    const base = archetype ? { ...existingProfile, ...archetype.profile } : { ...existingProfile };
    return {
      ...base,
      ...(archetypeId ? { archetype: archetypeId } : {}),
      mobility: {
        ...base.mobility,
        ...(pace ? { preferredDailyPace: pace } : {}),
      },
      comfort: {
        ...base.comfort,
        ...(budget ? { budgetLevel: budget } : {}),
      },
      interests: {
        ...base.interests,
        ...(interests.length > 0 ? { categories: interests } : {}),
      },
    };
  }, [archetypeId, budget, existingProfile, interests, pace]);

  useEffect(() => {
    if (stage === "signup") return;
    try {
      sessionStorage.setItem(FUNNEL_STORAGE_KEY, JSON.stringify({
        stage,
        archetypeId,
        pace,
        budget,
        interests,
        ownerId: isAuthenticated ? user?.id : null,
        saveAfterAuth,
      }));
    } catch {
      // The funnel works without browser storage; progress just will not survive a refresh.
    }
  }, [archetypeId, budget, interests, isAuthenticated, pace, saveAfterAuth, stage, user?.id]);

  useEffect(() => {
    if (isLoading || !isAuthenticated || initialRestorationValidated.current) return;
    initialRestorationValidated.current = true;
    if (savedDraft.stage === "signup" || saveAfterAuth) return;
    if (!savedDraft.ownerId || savedDraft.ownerId !== user?.id) {
      setStage("signup");
      setArchetypeId(null);
      setPace(null);
      setBudget(null);
      setInterests([]);
      try {
        sessionStorage.removeItem(FUNNEL_STORAGE_KEY);
      } catch {
        // The reset still protects the signed-in profile when storage is unavailable.
      }
    }
  }, [isAuthenticated, isLoading, saveAfterAuth, savedDraft.ownerId, savedDraft.stage, user?.id]);

  useEffect(() => {
    if (stage === "signup") return;
    const focusFrame = requestAnimationFrame(() => headingRef.current?.focus());
    return () => cancelAnimationFrame(focusFrame);
  }, [stage]);

  const savePreferences = useCallback(async () => {
    if (!isAuthenticated || isSaving) return true;

    setIsSaving(true);
    setSaveError("");
    try {
      const response = await apiRequest("PATCH", "/api/auth/user", { travelerProfile: draftProfile });
      const updatedUser = await response.json();
      seedAuthUserCache(updatedUser, user?.id);
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      setPreferencesSaved(true);
      setSaveAfterAuth(false);
      try {
        sessionStorage.removeItem(FUNNEL_STORAGE_KEY);
      } catch {
        // Saving to the profile succeeded even if the temporary browser draft cannot be cleared.
      }
      return true;
    } catch {
      setSaveError("We could not save your preferences yet. You can still explore, or try again.");
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [draftProfile, isAuthenticated, isSaving, user?.id]);

  const goBack = () => {
    const current = STAGE_ORDER.indexOf(stage);
    if (current <= 0) {
      setStage("signup");
      return;
    }
    setStage(STAGE_ORDER[current - 1]);
  };

  const toggleInterest = (interest: string) => {
    setInterests((current) =>
      current.includes(interest)
        ? current.filter((item) => item !== interest)
        : [...current, interest].slice(0, 8),
    );
  };

  const completeQuiz = async () => {
    if (isAuthenticated) {
      const saved = await savePreferences();
      if (!saved) return;
    }
    setStage("done");
  };

  const continueWithAccount = (path: "/sign-in" | "/sign-up") => {
    setSaveAfterAuth(true);
    try {
      sessionStorage.setItem(FUNNEL_STORAGE_KEY, JSON.stringify({
        stage,
        archetypeId,
        pace,
        budget,
        interests,
        ownerId: null,
        saveAfterAuth: true,
      }));
    } catch {
      // Redirecting to authentication still works; only the resume convenience is unavailable.
    }
    navigate(`${path}?returnTo=%2Flandingwithnewcrew`);
  };

  const showOnboarding = stage !== "signup";

  return (
    <div className="troopn-landing min-h-screen bg-funnel-ink text-funnel-sand">
      <PageMeta
        title="TROOPN — Set your travel preferences"
        description="Tell Troopn how you like to travel, then explore experiences or plan a trip with AI."
        path="/landingwithnewcrew"
        jsonLd={FUNNEL_JSONLD}
        track={{ pageType: "landing" }}
      />
      <a href="#funnel-content" className="skip-link">Skip to main content</a>
      <header className="mx-auto flex w-full max-w-6xl items-center justify-between px-5 py-5 sm:px-8 sm:py-7" data-testid="funnel-header">
        <a href="/" className="flex items-center gap-2 text-sm font-bold tracking-[0.2em] text-funnel-sand" aria-label="Troopn home">
          <span className="flex h-8 w-8 items-center justify-center rounded-full border border-funnel-sand/30 text-xs tracking-normal">T</span>
          TROOPN
        </a>
        <button
          type="button"
          onClick={() => continueWithAccount("/sign-in")}
          className="h-9 rounded-full border border-funnel-sand/20 px-4 text-xs font-semibold text-funnel-sand hover:bg-funnel-sand/10"
          data-testid="button-funnel-login"
        >
          Log in
        </button>
      </header>
      <main id="funnel-content">
        {!showOnboarding && <FunnelHero onSignupComplete={() => setStage("intro")} />}

        {showOnboarding && (
          <section className="mx-auto flex min-h-[calc(100svh-92px)] w-full max-w-3xl items-center px-5 py-12 sm:px-8 sm:py-20">
            <div className="w-full rounded-3xl bg-funnel-sand p-6 text-funnel-ink shadow-2xl shadow-black/20 sm:p-10">
              {stage !== "done" && (
                <button
                  type="button"
                  onClick={goBack}
                  className="mb-6 inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-funnel-ink hover:text-funnel-coral"
                  data-testid="button-funnel-back"
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                  Back
                </button>
              )}
              <ProgressLabel stage={stage} />
              <p className="sr-only" aria-live="polite">Travel preferences, step {Math.max(STAGE_ORDER.indexOf(stage) + 1, 1)} of 5.</p>

              {stage === "intro" && (
                <div className="max-w-xl">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-funnel-ink text-funnel-sand">
                    <Compass className="h-6 w-6" aria-hidden="true" />
                  </span>
                  <h1 ref={headingRef} tabIndex={-1} className="mt-6 text-3xl font-bold tracking-[-0.04em] sm:text-5xl">Set up your travel preferences</h1>
                  <p className="mt-5 text-lg leading-8 text-funnel-muted">
                    This takes about two minutes. Your answers help Troopn show you trips, events, and AI planning ideas that fit the way you actually like to travel.
                  </p>
                  <Button className="mt-8 min-h-12 w-full bg-funnel-coral text-white hover:bg-funnel-coral-strong sm:w-auto" onClick={() => setStage("style")} data-testid="button-funnel-start-preferences">
                    Let’s do it
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              )}

              {stage === "style" && (
                <div>
                  <p className="text-sm font-bold uppercase tracking-[0.18em] text-funnel-muted">Your style</p>
                  <h1 ref={headingRef} tabIndex={-1} className="mt-3 text-3xl font-bold tracking-[-0.04em] sm:text-4xl">What kind of traveler are you?</h1>
                  <p className="mt-3 text-base leading-7 text-funnel-muted">Pick the description that feels closest. You can change this anytime.</p>
                  <div className="mt-7 grid gap-3 sm:grid-cols-2">
                    {TRAVELER_ARCHETYPES.map((archetype) => {
                      const Icon = ARCHETYPE_ICONS[archetype.icon] ?? Compass;
                      const selected = archetypeId === archetype.id;
                      return (
                        <button
                          key={archetype.id}
                          type="button"
                          onClick={() => setArchetypeId(archetype.id)}
                          aria-pressed={selected}
                          className={`min-h-24 rounded-2xl border p-4 text-left transition-colors ${
                            selected
                              ? "border-funnel-coral bg-funnel-coral/10"
                              : "border-funnel-ink/15 bg-white hover:border-funnel-coral"
                          }`}
                          data-testid={`button-funnel-archetype-${archetype.id}`}
                        >
                          <Icon className="h-5 w-5" aria-hidden="true" />
                          <span className="mt-3 block font-bold">{archetype.name}</span>
                          <span className="mt-1 block text-sm leading-5 text-funnel-muted">{archetype.description}</span>
                        </button>
                      );
                    })}
                  </div>
                  <Button className="mt-8 min-h-12 w-full bg-funnel-coral text-white hover:bg-funnel-coral-strong" onClick={() => setStage("basics")} disabled={!archetypeId} data-testid="button-funnel-style-next">
                    Continue
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  {!archetypeId && <p className="mt-3 text-center text-sm text-funnel-muted">Choose one style to continue.</p>}
                </div>
              )}

              {stage === "basics" && (
                <div>
                  <p className="text-sm font-bold uppercase tracking-[0.18em] text-funnel-muted">Pace and comfort</p>
                  <h1 ref={headingRef} tabIndex={-1} className="mt-3 text-3xl font-bold tracking-[-0.04em] sm:text-4xl">How do you like to move through a trip?</h1>
                  <div className="mt-8 space-y-7">
                    <div>
                      <h2 className="font-bold">Daily pace</h2>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Chip selected={pace === "slow"} onClick={() => setPace("slow")} label="Slow and spacious" />
                        <Chip selected={pace === "moderate"} onClick={() => setPace("moderate")} label="A balanced mix" />
                        <Chip selected={pace === "aggressive"} onClick={() => setPace("aggressive")} label="See it all" />
                      </div>
                    </div>
                    <div>
                      <h2 className="font-bold">Comfort and budget</h2>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Chip selected={budget === "budget"} onClick={() => setBudget("budget")} label="Keep it lean" />
                        <Chip selected={budget === "moderate"} onClick={() => setBudget("moderate")} label="Comfortable balance" />
                        <Chip selected={budget === "premium"} onClick={() => setBudget("premium")} label="Premium touches" />
                        <Chip selected={budget === "luxury"} onClick={() => setBudget("luxury")} label="Go all out" />
                      </div>
                    </div>
                  </div>
                  <Button className="mt-8 min-h-12 w-full bg-funnel-coral text-white hover:bg-funnel-coral-strong" onClick={() => setStage("interests")} disabled={!pace || !budget} data-testid="button-funnel-basics-next">
                    Continue
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  {(!pace || !budget) && <p className="mt-3 text-center text-sm text-funnel-muted">Choose a pace and comfort level to continue.</p>}
                </div>
              )}

              {stage === "interests" && (
                <div>
                  <p className="text-sm font-bold uppercase tracking-[0.18em] text-funnel-muted">What pulls you in</p>
                  <h1 ref={headingRef} tabIndex={-1} className="mt-3 text-3xl font-bold tracking-[-0.04em] sm:text-4xl">What do you love on a trip?</h1>
                  <p className="mt-3 text-base leading-7 text-funnel-muted">Pick up to eight. Skip this if you’d rather browse first.</p>
                  <div className="mt-7 flex flex-wrap gap-2">
                    {QUIZ_INTERESTS.map((interest) => (
                      <Chip
                        key={interest}
                        selected={interests.includes(interest)}
                        onClick={() => toggleInterest(interest)}
                        label={interest.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase())}
                      />
                    ))}
                  </div>
                  <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                    <Button variant="outline" className="min-h-12 flex-1 border-funnel-ink/20" onClick={completeQuiz} disabled={isSaving}>
                      Skip for now
                    </Button>
                    <Button className="min-h-12 flex-1 bg-funnel-coral text-white hover:bg-funnel-coral-strong" onClick={completeQuiz} disabled={isSaving} data-testid="button-funnel-finish">
                      {isSaving ? "Saving preferences" : "Save my preferences"}
                      {isSaving ? <Loader2 className="ml-2 h-4 w-4 animate-spin" /> : <Check className="ml-2 h-4 w-4" />}
                    </Button>
                  </div>
                  {saveError && <p className="mt-3 text-sm font-medium text-funnel-error" role="alert">{saveError}</p>}
                </div>
              )}

              {stage === "done" && (
                <div>
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-funnel-coral text-white">
                    <Check className="h-6 w-6" aria-hidden="true" />
                  </span>
                  <h1 ref={headingRef} tabIndex={-1} className="mt-6 text-3xl font-bold tracking-[-0.04em] sm:text-5xl">
                    {preferencesSaved ? "Your preferences are saved." : "You’re ready to explore."}
                  </h1>
                  <p className="mt-4 max-w-2xl text-lg leading-8 text-funnel-muted">
                    {isSaving
                      ? "Saving your preferences to your Troopn profile…"
                      : preferencesSaved
                        ? "We’ll use them to make fit scores, discovery, and AI trip planning feel more like you."
                        : isAuthenticated
                          ? "Save these answers to your profile when you’re ready, or choose a path below."
                          : "Choose a path below. You can create an account whenever you’re ready to keep these preferences with your profile."}
                  </p>
                  {saveError && <p className="mt-3 text-sm font-medium text-funnel-error" role="alert">{saveError}</p>}
                  {isAuthenticated && !preferencesSaved && (
                    <Button
                      className="mt-6 min-h-12 bg-funnel-coral text-white hover:bg-funnel-coral-strong"
                      onClick={() => void savePreferences()}
                      disabled={isSaving}
                      data-testid="button-funnel-save-profile"
                    >
                      {isSaving ? "Saving preferences" : "Save preferences to my profile"}
                      {isSaving ? <Loader2 className="ml-2 h-4 w-4 animate-spin" /> : <Check className="ml-2 h-4 w-4" />}
                    </Button>
                  )}
                  <div className="mt-8 grid gap-4 sm:grid-cols-2">
                    <button
                      type="button"
                      onClick={() => navigate("/explore")}
                      className="group min-h-48 rounded-2xl border border-funnel-ink/15 bg-white p-6 text-left transition-colors hover:border-funnel-coral"
                      data-testid="button-funnel-explore"
                    >
                      <Compass className="h-7 w-7 text-funnel-coral" aria-hidden="true" />
                      <span className="mt-8 block text-xl font-bold">Explore what’s happening</span>
                      <span className="mt-2 block text-sm leading-6 text-funnel-muted">Browse festivals, local events, routes, and trips other people are excited about.</span>
                      <span className="mt-5 inline-flex items-center text-sm font-bold text-funnel-coral">Explore now <ArrowRight className="ml-1 h-4 w-4" /></span>
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(getFunnelPlannerDestination(isAuthenticated))}
                      className="group min-h-48 rounded-2xl bg-funnel-ink p-6 text-left text-funnel-sand transition-colors hover:bg-funnel-ink-raised"
                      data-testid="button-funnel-create-trip"
                    >
                      <Sparkles className="h-7 w-7 text-funnel-coral" aria-hidden="true" />
                      <span className="mt-8 block text-xl font-bold">Build your own with AI</span>
                      <span className="mt-2 block text-sm leading-6 text-funnel-sand/65">Tell us where and when. Troopn can help turn your idea into a trip plan.</span>
                      <span className="mt-5 inline-flex items-center text-sm font-bold text-funnel-coral">Start planning <ArrowRight className="ml-1 h-4 w-4" /></span>
                    </button>
                  </div>
                  {!isAuthenticated && !isLoading && (
                    <div className="mt-7 rounded-2xl border border-funnel-ink/10 bg-funnel-ink/5 p-5">
                      <p className="font-bold">Want to keep your preferences?</p>
                      <p className="mt-1 text-sm leading-6 text-funnel-muted">Create an account, then we’ll add the preferences you just set to your Troopn profile.</p>
                      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                        <button type="button" onClick={() => continueWithAccount("/sign-up")} className="inline-flex min-h-11 items-center justify-center rounded-xl bg-funnel-coral px-4 text-sm font-bold text-white hover:bg-funnel-coral-strong">
                          Create a free account
                        </button>
                        <button type="button" onClick={() => continueWithAccount("/sign-in")} className="inline-flex min-h-11 items-center justify-center rounded-xl border border-funnel-ink/20 px-4 text-sm font-bold text-funnel-ink hover:border-funnel-coral">
                          I already have an account
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        )}

        {!showOnboarding && <FunnelHowItWorks />}
      </main>
      <footer className="mx-auto flex w-full max-w-6xl items-center justify-between border-t border-funnel-sand/10 px-5 py-7 text-xs text-funnel-muted sm:px-8">
        <span>© {new Date().getFullYear()} Troopn</span>
        <span>Travel together, before you fly</span>
      </footer>
    </div>
  );
}
